import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  CheckCircle, XCircle, Loader2, Play, Users, Database, AlertTriangle, Shield, ClipboardCheck
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const RolesTeamTestsSection = () => {
  const [results, setResults] = useState(null);
  const [comprehensiveResults, setComprehensiveResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comprehensiveLoading, setComprehensiveLoading] = useState(false);

  const runTests = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/health/tests/roles-team-flow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      setResults(data);
      
      if (data.summary?.passRate === 100) {
        toast.success(`Roles & Team: All ${data.summary.total} tests passed!`);
      } else {
        toast.warning(`Roles & Team: ${data.summary.passed}/${data.summary.total} tests passed`);
      }
    } catch (error) {
      toast.error('Failed to run roles & team tests');
    } finally {
      setLoading(false);
    }
  };

  const runComprehensiveTests = async () => {
    setComprehensiveLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/health/tests/roles-comprehensive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      const data = await response.json();
      setComprehensiveResults(data);
      
      if (data.summary?.passRate === 100) {
        toast.success(`Roles Audit: All ${data.summary.total} tests passed!`);
      } else {
        toast.warning(`Roles Audit: ${data.summary.passed}/${data.summary.total} tests passed`);
      }
    } catch (error) {
      toast.error('Failed to run roles comprehensive tests');
    } finally {
      setComprehensiveLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'passed': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failed': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'skipped': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default: return <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />;
    }
  };

  const renderTestResults = (testData, title) => (
    <div className="space-y-4">
      <div className={`p-3 rounded-lg ${
        testData.summary?.passRate === 100 
          ? 'bg-green-100 border border-green-300' 
          : 'bg-yellow-100 border border-yellow-300'
      }`}>
        <div className="flex items-center justify-between">
          <span className="font-medium">
            {testData.summary?.passed}/{testData.summary?.total} tests passed
          </span>
          <span className={`text-lg font-bold ${
            testData.summary?.passRate === 100 ? 'text-green-700' : 'text-yellow-700'
          }`}>
            {testData.summary?.passRate}%
          </span>
        </div>
      </div>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {testData.tests?.map((test, index) => (
          <div 
            key={index}
            className={`p-3 rounded-lg border ${
              test.status === 'passed' ? 'bg-green-50 border-green-200' 
              : test.status === 'failed' ? 'bg-red-50 border-red-200'
              : 'bg-yellow-50 border-yellow-200'
            }`}
          >
            <div className="flex items-start gap-3">
              {getStatusIcon(test.status)}
              <div className="flex-1">
                <span className="font-medium text-sm">{test.name}</span>
                <p className="text-xs text-gray-600 mt-1">{test.message}</p>
                {test.endpoint && (
                  <p className="text-xs text-gray-500 mt-1 font-mono">{test.endpoint}</p>
                )}
                {test.database && (
                  <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                    <Database className="w-3 h-3" />{test.database}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {testData.cleanup && (
        <div className={`p-2 rounded text-xs ${
          testData.cleanup.status === 'success' ? 'bg-gray-100 text-gray-600' : 'bg-red-100 text-red-600'
        }`}>
          Cleanup: {testData.cleanup.status} {testData.cleanup.message && `- ${testData.cleanup.message}`}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Roles Comprehensive Test (Tab Audit) */}
      <Card className="border-indigo-200 bg-indigo-50/30">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-indigo-600" />
              <CardTitle className="text-lg text-indigo-900">
                Roles Comprehensive (Tab Audit)
              </CardTitle>
            </div>
            <Button
              onClick={runComprehensiveTests}
              disabled={comprehensiveLoading}
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {comprehensiveLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running...</>
              ) : (
                <><Play className="w-4 h-4 mr-2" />Run Audit</>
              )}
            </Button>
          </div>
          <p className="text-sm text-indigo-700 mt-1">
            CRUD • Activity Logging • Edge Cases • Multi-Tenant Isolation • DB Verification
          </p>
        </CardHeader>
        
        <CardContent>
          {comprehensiveResults ? (
            renderTestResults(comprehensiveResults, "Roles Comprehensive")
          ) : (
            <div className="text-center py-6 text-gray-500">
              <ClipboardCheck className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Click &quot;Run Audit&quot; to verify roles with activity logging</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Roles & Team Management Test */}
      <Card className="border-purple-200 bg-purple-50/30">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              <CardTitle className="text-lg text-purple-900">
                Roles & Team Management (PostgreSQL)
              </CardTitle>
            </div>
            <Button
              onClick={runTests}
              disabled={loading}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running...</>
              ) : (
                <><Play className="w-4 h-4 mr-2" />Run Tests</>
              )}
            </Button>
          </div>
          <p className="text-sm text-purple-700 mt-1">
            Role CRUD → Team Member CRUD → Availability Check → Schedule Query
          </p>
        </CardHeader>
        
        <CardContent>
          {results ? (
            renderTestResults(results, "Roles & Team")
          ) : (
            <div className="text-center py-6 text-gray-500">
              <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Click &quot;Run Tests&quot; to verify roles & team management</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RolesTeamTestsSection;
